import { useState } from 'react'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { RefreshCw, AlertCircle, Shield, Plus, Users } from 'lucide-react'
import { GroupsTable } from '@/modules/users/components/GroupsTable'
import { GroupsFilters } from '@/modules/users/components/GroupsFilters'
import { GroupFormDialog } from '@/modules/users/components/GroupFormDialog'
import {
  useGroupExtensions,
  useCreateGroup,
  useUpdateGroupExtension,
  useTogglePermiso,
  useDeleteGroupExtension,
} from '@/modules/users/hooks/useGroups'
import type { GroupExtensionsFilters, GroupCreateInput, GroupExtensionUpdateInput, GroupExtensionListItem } from '@/modules/users/types/group'

export function GroupsPage() {
  useDocumentTitle('Grupos')
  const [filters, setFilters] = useState<GroupExtensionsFilters>({
    page: 1,
    page_size: 25,
    laboratorio: false, // Solo grupos NO asociados a laboratorios
  })
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<GroupExtensionListItem | null>(null)

  // Queries
  const { data: groupsData, isLoading, error, isError, refetch } = useGroupExtensions(filters)

  // Mutations
  const createGroup = useCreateGroup()
  const updateGroupExtension = useUpdateGroupExtension()
  const togglePermiso = useTogglePermiso()
  const deleteGroupExtension = useDeleteGroupExtension()

  // Handlers
  const handleFiltersChange = (newFilters: GroupExtensionsFilters) => {
    setFilters(newFilters)
  }

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      page_size: 25,
      laboratorio: false, // Mantener el filtro de laboratorio
    })
  }

  const handleTogglePermiso = async (extensionId: number) => {
    await togglePermiso.mutateAsync(extensionId)
  }

  const handleDelete = async (extensionId: number) => {
    if (
      confirm(
        '¿Está seguro de que desea eliminar esta extensión? Se recreará automáticamente si el grupo existe.'
      )
    ) {
      await deleteGroupExtension.mutateAsync(extensionId)
    }
  }

  const handleViewGroup = (extensionId: number) => {
    // Buscar el grupo y abrir modal en modo solo lectura (por ahora igual que editar)
    const group = groupsData?.results.find((g) => g.id === extensionId)
    if (group) {
      setEditingGroup(group)
      setIsEditModalOpen(true)
    }
  }

  const handleEditGroup = (extensionId: number) => {
    // Buscar el grupo en los datos actuales
    const group = groupsData?.results.find((g) => g.id === extensionId)
    if (group) {
      setEditingGroup(group)
      setIsEditModalOpen(true)
    }
  }

  const handleCreateGroup = async (data: GroupCreateInput) => {
    // Asegurar que laboratorio esté en false para grupos normales
    await createGroup.mutateAsync({ ...data, laboratorio: false })
    setIsCreateModalOpen(false)
  }

  const handleUpdateGroup = async (extensionId: number, data: GroupExtensionUpdateInput) => {
    await updateGroupExtension.mutateAsync({ extensionId, data })
    setIsEditModalOpen(false)
    setEditingGroup(null)
  }

  const handleEditModalClose = (open: boolean) => {
    setIsEditModalOpen(open)
    if (!open) {
      setEditingGroup(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-6 w-6 text-teal-500" />
          Grupos
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setIsCreateModalOpen(true)} className="bg-teal-800 hover:bg-teal-900 text-white">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Grupo
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-teal-500/5 border border-teal-500/20 text-sm text-muted-foreground">
        <Shield className="h-4 w-4 text-teal-500 flex-shrink-0" />
        Aquí puedes gestionar grupos generales del sistema (NO laboratorios). Los grupos
        pueden tener permisos especiales configurables. Para gestionar laboratorios,
        ve a la sección "Laboratorios" en el menú.
      </div>

      {/* Filters */}
      <GroupsFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
            <h3 className="mt-4 text-lg font-semibold text-destructive">Error al cargar grupos</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {(error as any)?.response?.data?.error ||
               'No tienes permisos para acceder a esta información. Contacta al administrador.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-4"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </div>
        ) : groupsData && groupsData.results.length > 0 ? (
          <div className="p-6">
            <GroupsTable
              groups={groupsData.results}
              onView={handleViewGroup}
              onEdit={handleEditGroup}
              onTogglePermiso={handleTogglePermiso}
              onDelete={handleDelete}
              hideLaboratorioToggle={true}
            />

            {/* Pagination */}
            {groupsData.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(filters.page! - 1) * filters.page_size! + 1} -{' '}
                  {Math.min(filters.page! * filters.page_size!, groupsData.count)} de{' '}
                  {groupsData.count} grupos
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
                    }
                    disabled={filters.page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">
                    Página {filters.page} de {groupsData.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
                    }
                    disabled={filters.page === groupsData.total_pages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron grupos</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Intenta ajustar los filtros o espera a que se creen grupos en Django
            </p>
          </div>
        )}
      </Card>

      {/* Create Group Modal */}
      <GroupFormDialog
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSubmit={handleCreateGroup}
        isSubmitting={createGroup.isPending}
        defaultLaboratorio={false}
        title="Crear Nuevo Grupo"
        hideLaboratorioField={true}
        mode="create"
      />

      {/* Edit Group Modal */}
      <GroupFormDialog
        open={isEditModalOpen}
        onOpenChange={handleEditModalClose}
        onSubmit={handleCreateGroup}
        onUpdate={handleUpdateGroup}
        isSubmitting={updateGroupExtension.isPending}
        defaultLaboratorio={false}
        hideLaboratorioField={true}
        mode="edit"
        editData={editingGroup}
      />
    </div>
  )
}
